import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button,
  CircularProgress, Alert, Chip, Tooltip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import { content } from '../api/client';

const CourseList = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([content.module(moduleId), content.courses(moduleId)])
      .then(([modRes, courseRes]) => {
        setModule(modRes.data);
        setCourses(courseRes.data);
      })
      .catch(() => setError('Data load failed'))
      .finally(() => setLoading(false));
  }, [moduleId]);

  const getQuizResult = (courseId) => {
    const key = `quiz_${moduleId}_${courseId}`;
    return JSON.parse(localStorage.getItem(key) || 'null');
  };

  const isCourseUnlocked = (index) => {
    if (index === 0) return true;
    const prevCourse = courses[index - 1];
    const prevResult = getQuizResult(prevCourse.id);
    return prevResult?.passed === true;
  };

  const allCoursesComplete = courses.length > 0 && courses.every(c => {
    const result = getQuizResult(c.id);
    return result?.passed;
  });

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom className="title-wrapper">
        {module?.icon && (
          <img src={module.icon} alt="" className="module-icon-inline" />
        )}
        Courses in {module?.title || moduleId}
      </Typography>

      {allCoursesComplete && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/module/${moduleId}/complete`)}>
              View Certificate
            </Button>
          }
        >
          You've completed all courses in this module!
        </Alert>
      )}

      <Grid container spacing={4}>
        {courses.map((course, index) => {
          const unlocked = isCourseUnlocked(index);
          const quizResult = getQuizResult(course.id);
          const quizPassed = quizResult?.passed;

          return (
            <Grid item xs={12} sm={6} md={4} key={course.id}>
              <Card
                className={`course-card ${moduleId}`}
                sx={{
                  opacity: unlocked ? 1 : 0.65,
                  position: 'relative',
                  filter: unlocked ? 'none' : 'grayscale(40%)'
                }}
              >
                {!unlocked && (
                  <Box sx={{
                    position: 'absolute', top: 10, right: 10,
                    bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%', p: 0.5
                  }}>
                    <LockIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                )}
                {quizPassed && (
                  <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28, bgcolor: 'white', borderRadius: '50%' }} />
                  </Box>
                )}
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75, fontWeight: 'bold' }}>
                      Course {index + 1}
                    </Typography>
                    {quizResult && !quizPassed && (
                      <Chip label={`Quiz: ${quizResult.score}%`} size="small" color="warning" variant="outlined"
                        sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }} />
                    )}
                    {quizPassed && (
                      <Chip label={`Quiz: ${quizResult.score}%`} size="small" color="success"
                        sx={{ bgcolor: 'rgba(76,175,80,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} />
                    )}
                  </Box>
                  <Typography variant="h6">{course.title}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {course.description}
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.75 }}>
                    {course.lessonCount || course.lessons?.length || 0} lessons
                  </Typography>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {unlocked ? (
                      <>
                        <Button
                          component={Link}
                          to={`/module/${moduleId}/course/${course.id}`}
                          variant="outlined"
                          size="small"
                        >
                          {quizPassed ? 'Review Lessons' : 'View Lessons'}
                        </Button>
                        <Tooltip title="Go directly to the course quiz">
                          <Button
                            component={Link}
                            to={`/module/${moduleId}/course/${course.id}/quiz`}
                            variant="outlined"
                            size="small"
                            startIcon={<QuizIcon />}
                          >
                            {quizPassed ? 'Retake Quiz' : 'Take Quiz'}
                          </Button>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title={`Pass the quiz for "${courses[index - 1]?.title}" to unlock`}>
                        <span>
                          <Button variant="outlined" size="small" disabled startIcon={<LockIcon />}>
                            Locked
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {allCoursesComplete && (
        <Box textAlign="center" mt={4}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(`/module/${moduleId}/complete`)}
            sx={{ px: 5, py: 1.5, fontSize: '1.1rem' }}
          >
            View Module Completion Page 🎉
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CourseList;
